import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Register.css";
import travelerLogin from "../assets/travelerLogin.png";
import hotelLogin from "../assets/hotelLogin.png";
import guideLogin from "../assets/guideLogin.png";
// Image assets for roles
// Modularized Components
import RegistrationForm from "../components/RegistrationForm.jsx";
import RoleSelector from "../components/RoleSelector.jsx";
import SocialLoginButtons from "../components/SocialLoginButtons.jsx";
import Navbar from "../components/Navbar";

// Service Methods
import {
  registerTraveler,
  registerGuide,
  updateGuideFiles,
  registerHotelOwner,
  updateHotelOwnerFiles,
  validateCityZip,
  validateNicDob,
  validateNicGender,
  validateCityDistrict,
  fetchRegistrationData,
} from "../services/userService";
import { uploadFile } from "../services/fileService";

// --- NEW IMPORT: Validation Utils ---
import FormValidators from "../utils/validation"; // Ensure this path matches your folder structure

// --- VALIDATION HELPERS ---
const isEmailValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const isPasswordStrong = (password) =>
  password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password);

const validateFormData = (
  role,
  formData,
  businessLogo,
  businessRegistration,
  nicFront,
  nicBack,
  billFront,
  billBack
) => {
  const { full_name, phone_number, email, password, dob, city, zip_code } =
    formData;

  // Common checks
  if (!full_name || !phone_number || !email || !password || !dob) {
    return "All required fields must be filled.";
  }
  if (!formData.nationality_id || !formData.country_id || !formData.gender_id) {
    return "All dropdown selections are required.";
  }
  if (!isEmailValid(email)) {
    return "Please enter a valid email address.";
  }
  if (!isPasswordStrong(password)) {
    return "Password must be at least 8 characters long and contain at least one uppercase letter and one number.";
  }

  const phoneClean = (phone_number || "").trim().replace(/\s+/g, "");
  if (!/^(\+?[1-9]\d{8,14}|0\d{9})$/.test(phoneClean)) {
    return "Invalid Mobile Number. Please enter a valid mobile number (e.g. +94771234567 or 0771234567).";
  }

 

  // 2. AGE VALIDATION (Integrated from FormValidators)
  if (role === "Traveler") {
    // Check if age is 17+
    const birthdayError = FormValidators.validateBirthdayForTraveler(dob);
    if (birthdayError) {
      return birthdayError; // Returns "You must be at least 17 years old"
    }
  } else {
    // Check if age is 20+ for Guide and Hotel Owner
    const birthdayError = FormValidators.validateBirthdayForGuidHotel(dob);
    if (birthdayError) {
      return birthdayError; // Returns "You must be at least 20 years old"
    }
  }

  // Role-specific checks
  if (role === "Hotel Owner") {
    if (
      !formData.business_name ||
      !formData.national_id ||
      !city ||
      !zip_code ||
      !formData.property_type_id ||
      !formData.district_id ||
      !formData.address
    ) {
      return "Hotel Owner: All business details are required.";
    }
    const addressError = FormValidators.validateAddress(formData.address);
    if (addressError) return addressError;
    const nicError = FormValidators.validateNIC(formData.national_id);
    if (nicError) return nicError;

    if (!nicFront || !nicBack || !billFront) {
      return "Hotel Owner: NIC (Front/Back) and Bill (Front) documents are required.";
    }
  }

  if (role === "Guide") {
    if (!formData.national_id || !formData.address || !city || !zip_code || !formData.district_id) {
      return "Guide: All personal and location details are required.";
    }
    const addressError = FormValidators.validateAddress(formData.address);
    if (addressError) return addressError;
    const nicError = FormValidators.validateNIC(formData.national_id);
    if (nicError) return nicError;
    
    if (!nicFront || !nicBack || !billFront) {
        return "Guide: NIC (Front/Back) and Bill (Front) documents are required.";
    }
  }

  return null;
};
// --- END VALIDATION HELPERS ---

function Register() {
  const [role, setRole] = useState("Traveler");
  const [logoName, setLogoName] = useState("");
  const [registrationName, setRegistrationName] = useState("");
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    full_name: "",
    phone_number: "",
    email: "",
    password: "",
    nationality_id: "",
    country_id: "",
    gender_id: "",
    dob: "",
    role_id: 5,
    national_id: "",
    business_name: "",
    zip_code: "",
    address: "",
    city: "",
    property_type_id: "",
    district_id: "",
    referral_code: "",
  });

  const [businessLogo, setBusinessLogo] = useState(null);
  const [businessRegistration, setBusinessRegistration] = useState(null);
  const [nicFront, setNicFront] = useState(null);
  const [nicBack, setNicBack] = useState(null);
  const [billFront, setBillFront] = useState(null);
  const [billBack, setBillBack] = useState(null);

  const [nicFrontName, setNicFrontName] = useState("");
  const [nicBackName, setNicBackName] = useState("");
  const [billFrontName, setBillFrontName] = useState("");
  const [billBackName, setBillBackName] = useState("");

  const [nationalities, setNationalities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [genders, setGenders] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [rolesList, setRolesList] = useState([]);

  const roleIds = { Traveler: 5, Guide: 2, "Hotel Owner": 3 };

  const getResolvedRoleId = (roleName) => {
    const found = rolesList.find(
      (r) => r.name && r.name.toLowerCase() === roleName.toLowerCase()
    );
    return found ? found.id : roleIds[roleName] || 5;
  };

  const roleImages = {
    Traveler: travelerLogin,
    "Hotel Owner": hotelLogin,
    Guide: guideLogin,
  };

  // --- Data Fetching Effect (Now uses service method) ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const { nationalities, countries, genders, propertyTypes, districts, roles } =
          await fetchRegistrationData();

        setNationalities(nationalities);
        setCountries(countries);
        setGenders(genders);
        setPropertyTypes(propertyTypes);
        setDistricts(districts);
        if (roles && Array.isArray(roles)) {
          setRolesList(roles);
        }
        if (countries && countries.length > 0 && (role === "Hotel Owner" || role === "Guide")) {
          const sl = countries.find((c) => c.name.toLowerCase().includes("sri lanka"));
          if (sl) {
            setFormData((prev) => ({ ...prev, country_id: sl.id }));
          }
        }
      } catch (err) {
        // Display generic service error message
        toast.error(err.message || "Failed to load initial data.");
      }
    };
    fetchData();
  }, [role]);

  // --- Handlers ---
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    setLogoName("");
    setRegistrationName("");
    setBusinessLogo(null);
    setBusinessRegistration(null);
    if (newRole === "Hotel Owner" || newRole === "Guide") {
      const sl = countries.find((c) => c.name.toLowerCase().includes("sri lanka"));
      if (sl) {
        setFormData((prev) => ({ ...prev, country_id: sl.id }));
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const name = e.target.name;

    if (file) {
      switch (name) {
        case "businessLogo":
          setBusinessLogo(file);
          setLogoName(file.name);
          break;
        case "businessRegistration":
          setBusinessRegistration(file);
          setRegistrationName(file.name);
          break;
        case "nicFront":
          setNicFront(file);
          setNicFrontName(file.name);
          break;
        case "nicBack":
          setNicBack(file);
          setNicBackName(file.name);
          break;
        case "billFront":
          setBillFront(file);
          setBillFrontName(file.name);
          break;
        case "billBack":
          setBillBack(file);
          setBillBackName(file.name);
          break;
        default:
          break;
      }
    }
  };

  // --- Submission Logic ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // 1. FRONT-END VALIDATION (Includes the new Age Check)
    const validationError = validateFormData(
      role,
      formData,
      businessLogo,
      businessRegistration,
      nicFront,
      nicBack,
      billFront,
      billBack
    );
    if (validationError) {
      toast.error(validationError);
      setIsLoading(false);
      return;
    }

    // 2. PREPARE FORM DATA
    const role_id = getResolvedRoleId(role);
    let finalFormData = { ...formData, role_id };

    if (finalFormData.dob) {
      finalFormData.date_of_birth = finalFormData.dob;
      delete finalFormData.dob;
    }

    try {
      if (role === "Traveler") {
        const travelerData = { ...finalFormData };
        travelerData.referral_code = travelerData.referral_code?.trim()
          ? travelerData.referral_code.trim()
          : null;
        await registerTraveler(travelerData);
        toast.success(
          "Registration successful! Please check your email for verification."
        );
        navigate("/email-verify", {
          state: { email: formData.email, role: role },
        });
      } else if (role === "Guide") {
        const isValidZipCity = await validateCityZip(formData.city, formData.zip_code);
        if (!isValidZipCity.isValid) {
          toast.error("Invalid city or zip code. Please correct the location details.");
          setIsLoading(false);
          return;
        }

        const genderId = parseInt(formData.gender_id, 10);
        const districtId = parseInt(formData.district_id, 10);

        // Validations
        const isValidNicDob = await validateNicDob(formData.national_id, formData.dob);
        if (!isValidNicDob) {
          toast.error("NIC and Date of Birth do not match. Please check and try again!");
          setIsLoading(false);
          return;
        }
        const isValidNicGender = await validateNicGender(formData.national_id, genderId);
        if (!isValidNicGender) {
          toast.error("NIC does not match the selected gender. Please check and try again!");
          setIsLoading(false);
          return;
        }
        const isValidCityDistrict = await validateCityDistrict(formData.city, districtId);
        if (!isValidCityDistrict) {
          toast.error(`The city "${formData.city}" does not belong to the selected district. Please correct and try again.`);
          setIsLoading(false);
          return;
        }
              
        // Step 1: Upload files first to ensure validity
        const nicFrontUrl = nicFront ? (await uploadFile(nicFront)).path : null;
        const nicBackUrl = nicBack ? (await uploadFile(nicBack)).path : null;
        const billFrontUrl = billFront ? (await uploadFile(billFront)).path : null;
        const billBackUrl = billBack ? (await uploadFile(billBack)).path : null;
        const businessRegistrationUrl = businessRegistration ? (await uploadFile(businessRegistration)).path : null;

        // Step 2: Register guide
        const registrationPayload = { ...finalFormData, gender_id: genderId, district_id: districtId };
        registrationPayload.referral_code = registrationPayload.referral_code?.trim() ? registrationPayload.referral_code.trim() : null;
        
        const regResponse = await registerGuide(registrationPayload);
        const userId = regResponse.userId;
        if (!userId) {
          throw new Error("Registration succeeded but did not return a user ID.");
        }

        // Step 3: Update user with files
        await updateGuideFiles({
          userId,
          nicFront: nicFrontUrl,
          nicBack: nicBackUrl,
          billFront: billFrontUrl,
          billBack: billBackUrl,
          businessRegistration: businessRegistrationUrl,
        });

        toast.success("Guide registration successful! Awaiting admin approval.");
        navigate("/email-verify", { state: { email: formData.email } });      
      } else if (role === "Hotel Owner") {
        const isValidZipCity = await validateCityZip(
          formData.city,
          formData.zip_code
        );
        if (!isValidZipCity.isValid) {
          toast.error(
            "Invalid city or zip code. Please correct the location details."
          );
          setIsLoading(false);
          return;
        }

        const genderId = parseInt(formData.gender_id, 10);
        const districtId = parseInt(formData.district_id, 10);

        // NIC + DOB Validation
        const isValidNicDob = await validateNicDob(
          formData.national_id,
          formData.dob
        );
        if (!isValidNicDob) {
          toast.error(
            "NIC and Date of Birth do not match. Please check and try again!"
          );
          setIsLoading(false);
          return;
        }

        // NIC + Gender Validation
        const isValidNicGender = await validateNicGender(
          formData.national_id,
          genderId
        );
        if (!isValidNicGender) {
          toast.error(
            "NIC does not match the selected gender. Please check and try again!"
          );
          setIsLoading(false);
          return;
        }

        // City + District Validation
        const isValidCityDistrict = await validateCityDistrict(
          formData.city,
          districtId
        );
        if (!isValidCityDistrict) {
          toast.error(
            `The city "${formData.city}" does not belong to the selected district. Please correct and try again.`
          );
          setIsLoading(false);
          return;
        }

        // Step 1: Upload all files first
        const nicFrontUrl = nicFront ? (await uploadFile(nicFront)).path : null;
        const nicBackUrl = nicBack ? (await uploadFile(nicBack)).path : null;
        const billFrontUrl = billFront
          ? (await uploadFile(billFront)).path
          : null;
        const billBackUrl = billBack ? (await uploadFile(billBack)).path : null;
        const businessRegistrationUrl = businessRegistration
          ? (await uploadFile(businessRegistration)).path
          : null;

        // Step 2: Register user and get ID
        const registrationPayload = {
          ...finalFormData,
          gender_id: genderId,
          district_id: districtId,
        };
        registrationPayload.referral_code =
          registrationPayload.referral_code?.trim()
            ? registrationPayload.referral_code.trim()
            : null;

        const regResponse = await registerHotelOwner(registrationPayload);
        const userId = regResponse.userId;

        if (!userId) {
          throw new Error(
            "Registration succeeded but did not return a user ID."
          );
        }

        // Step 3: Update user with file URLs
        await updateHotelOwnerFiles({
          userId,
          nicFrontUrl,
          nicBackUrl,
          billFrontUrl,
          billBackUrl,
          businessRegistrationUrl,
        });

        toast.success(
          "Hotel registration successful! Awaiting admin approval."
        );
        navigate("/email-verify", {
          state: { email: formData.email, role: role },
        });
      }
    } catch (err) {
      const message =
        err.message || "Registration failed due to a server error.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="agent-page-premium">
      <Navbar />

      <ToastContainer
        position="top-right"
        autoClose={5000}
        theme="colored"
      />

      <div className="premium-hero-mini">
        <div className="hero-content">
          <h1>Join the Soloway Community</h1>
          <p>Choose your path and start your journey with us today.</p>
        </div>
      </div>

      <div className="premium-container-wide">
        <div className="premium-glass-card">
          <div className="register-header-premium">
            <RoleSelector role={role} setRole={handleRoleChange} />
          </div>

          <RegistrationForm
            role={role}
            formData={formData}
            isLoading={isLoading}
            logoName={logoName}
            registrationName={registrationName}
            nicFrontName={nicFrontName}
            nicBackName={nicBackName}
            billFrontName={billFrontName}
            billBackName={billBackName}
            nationalities={nationalities}
            countries={countries}
            genders={genders}
            propertyTypes={propertyTypes}
            districts={districts}
            handleChange={handleChange}
            handleFileChange={handleFileChange}
            handleSubmit={handleSubmit}
          />

          <div className="register-footer-premium">
            <p className="signin-text">
              Already have an account? <a href="/login">Sign In</a>
            </p>
            
            <div className="divider-premium">
              <span>or connect with</span>
            </div>

            <SocialLoginButtons />
          </div>
        </div>
      </div>
    </div>
  );
}


export default Register;
